<template>
  <Breadcrumbs title="create agent" />
  <Card9 colClass="col-12" cardhaderClass="pb-0" title="Create Agent" desc="">
    <BaseForm v-model="formData" :fields="formFields" />

    <!-- Role MultiSelect -->
    <VueMultiselect
      v-model="formData.roles"
      :options="roleOptions"
      :multiple="true"
      :close-on-select="false"
      placeholder="Select Roles"
      label="title"
      track-by="_id"
      class="mt-3"
    />

    <!-- Tools MultiSelect -->
    <VueMultiselect
      v-model="formData.tools"
      :options="toolOptions"
      :multiple="true"
      :close-on-select="false"
      placeholder="Select Tools"
      label="title"
      track-by="_id"
      class="mt-3"
    />

    <!-- Links Input -->
    <div class="mt-4">
      <BaseForm v-model="formData" :fields="linkFields" />
      <div class="col-12 text-end mt-1">
        <button type="button" class="btn btn-primary" @click="handleAddLink">
          Add Link
        </button>
      </div>
    </div>

    <!-- Links Preview -->
    <div v-if="links.length" class="mt-2">
      <div class="row g-3">
        <div
          v-for="link in links"
          :key="link._id"
          class="col-12 col-sm-6 col-md-3"
        >
          <div class="card h-100">
            <div
              class="card-body py-2 px-3 d-flex align-items-center justify-content-between"
            >
              <!-- Link Text -->
              <a
                :href="link.url"
                target="_blank"
                class="text-truncate d-block"
                style="max-width: 150px"
              >
                {{ link.url }}
              </a>

              <!-- Remove button -->
              <button
                type="button"
                class="btn p-2 btn-sm btn-outline-danger"
                @click="deleteLink(link._id)"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Attachments -->
    <div class="mt-4">
      <BaseForm
        v-model="formData"
        :fields="attachmentFields"
        @update:modelValue="handleAdd"
      />
    </div>

    <!-- File Preview List -->
    <div v-if="attachments.length" class="mt-2">
      <div class="row g-3">
        <div
          v-for="file in attachments"
          :key="file._id"
          class="col-12 col-sm-6 col-md-3"
        >
          <div class="card h-100">
            <div class="card-body py-1 px-2 d-flex align-items-center gap-3">
              <!-- Preview (image/pdf/icon) -->
              <div>
                <img
                  v-if="file.isImage"
                  :src="file.preview"
                  alt="Preview"
                  class="img-thumbnail"
                  style="width: 60px; height: 60px; object-fit: cover"
                />
                <embed
                  v-else-if="file.isPdf"
                  :src="file.preview"
                  type="application/pdf"
                  style="width: 60px; height: 60px"
                />
                <i
                  v-else
                  class="bi bi-file-earmark-text"
                  style="font-size: 2rem; color: #6c757d"
                ></i>
              </div>

              <!-- File name -->
              <div class="flex-grow-1">
                <span class="text-truncate d-block" style="max-width: 150px">
                  {{ file.attachmentName }}
                </span>
              </div>

              <!-- Remove button -->
              <button
                type="button"
                class="btn p-2 btn-sm btn-outline-danger"
                @click="deleteRow(file._id)"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 text-end mt-4">
      <button type="button" class="btn btn-primary" @click="handleSubmit">
        Submit
      </button>
    </div>
  </Card9>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, onMounted, ref } from "vue";
import * as bootstrap from "bootstrap";
import apiService from "@/services/apiService";
import { toast } from "vue3-toastify";

import BaseForm from "@/components/commen/modal/BaseForm.vue";
import VueMultiselect from "vue-multiselect";
const Card9 = defineAsyncComponent(
  () => import("@/components/commen/card/cardData9.vue")
);

// Form data
const formData = ref({
  title: "",
  roles: [] as any[],
  tools: [] as any[],
  description: "",
  link: "", // ✅ single link input
  attachment: [] as any[],
});

// Options for Role & Tools
const roleOptions = ref<any[]>([]);
const toolOptions = ref<any[]>([]);

// Attachments
const attachments = ref<any[]>([]);

// Links
const links = ref<any[]>([]);

// Fields for BaseForm
const formFields = [
  {
    key: "title",
    label: "Title",
    type: "text",
    placeholder: "Enter title",
    required: true,
  },
  {
    key: "description",
    label: "Description",
    type: "textarea",
    placeholder: "Enter description",
    rows: 4,
    required: true,
  },
];

// ✅ Fields for Links
const linkFields = [
  {
    key: "link",
    label: "Link",
    type: "text",
    placeholder: "Enter link (https://...)",
    required: true,
  },
];

// ✅ Fields for Attachments
const attachmentFields = [
  {
    key: "attachment",
    label: "Attachment",
    type: "file",
    placeholder: "Upload attachment",
    required: true,
  },
];

// API calls
const getRoles = async () => {
  try {
    const response = await apiService.get("common/roles");
    if (response) roleOptions.value = response;
  } catch (error) {
    console.error("❌ API Error:", error);
  }
};
const getTools = async () => {
  try {
    const response = await apiService.get("common/tools");
    if (response) toolOptions.value = response;
  } catch (error) {
    console.error("❌ API Error:", error);
  }
};
onMounted(() => {
  getRoles();
  getTools();
});

// ✅ Add Link
function handleAddLink() {
  const link = formData.value.link?.trim();
  if (!link) {
    return;
  }

  links.value.push({
    _id: Date.now().toString(),
    url: link,
  });

  formData.value.link = ""; // reset field
}

// ✅ Delete Link
function deleteLink(id: string) {
  links.value = links.value.filter((l) => l._id !== id);
}

function handleAdd(newValue: any) {
  let fileInput = newValue.attachment;
  if (!fileInput) return;

  // Normalize to single File
  let file: File;
  if (fileInput instanceof FileList) {
    file = fileInput[0];
  } else if (Array.isArray(fileInput)) {
    file = fileInput[0];
  } else {
    file = fileInput;
  }

  if (!(file instanceof File)) {
    console.error("Not a valid File:", fileInput);
    return;
  }

  const isImage = file.type.startsWith("image/");
  const isPdf = file.type === "application/pdf";
  const previewUrl = URL.createObjectURL(file);

  attachments.value.push({
    _id: Date.now().toString(),
    attachment: file,
    attachmentName: file.name,
    preview: previewUrl,
    isImage,
    isPdf,
  });

  formData.value.attachment = null; // reset
}

// ✅ Delete Attachment
function deleteRow(id: string) {
  const file = attachments.value.find((f) => f._id === id);
  if (file?.preview) URL.revokeObjectURL(file.preview);

  attachments.value = attachments.value.filter((f) => f._id !== id);
}

// ✅ Handle Submit
async function handleSubmit() {
  try {
    // ✅ Basic validation
    if (!formData.value.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.value.description.trim()) {
      toast.error("Description is required");
      return;
    }

    // ✅ Step 1: Upload attachments first
    const uploadedAttachments: string[] = [];
    if (attachments.value.length > 0) {
      try {
        for (const file of attachments.value) {
          if (file.attachment instanceof File) {
            const uploadedUrl: string = await apiService.uploadFile(
              "upload/file-common",
              file.attachment
            );
            uploadedAttachments.push(uploadedUrl);
          }
        }
      } catch (err) {
        console.error("❌ File upload failed:", err);
        toast.error("Failed to upload attachments");
        return;
      }
    }

    // ✅ Step 2: Build final JSON payload (not FormData)
    const payload = {
      title: formData.value.title.trim(),
      description: formData.value.description.trim(),
      roles: formData.value.roles.map((r: any) => r._id),
      tools: formData.value.tools.map((t: any) => t._id),
      links: links.value.map((l: any) => l.url),
      attachments: uploadedAttachments, // ✅ uploaded file links
    };

    // ✅ Step 3: Send to API
    const response = await apiService.post("common/agents", payload);

    if (response) {
      toast.success("Agent created successfully!");

      // Reset everything
      formData.value = {
        title: "",
        roles: [],
        tools: [],
        description: "",
        link: "",
        attachment: [],
      };
      links.value = [];
      attachments.value.forEach((file: any) => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
      attachments.value = [];
    }
  } catch (error) {
    console.error("❌ Submit Error:", error);
    toast.error("Failed to create agent. Please try again.");
  }
}

</script>
